/**
 * Board growth levels — frontend mirror of /app/backend/board_levels.py.
 *
 * Single source of truth for:
 *   - tile cap per level (how many tiles the child board renders)
 *   - whether Sentence Builder shows at that level
 *   - human-readable labels for the picker
 *   - grandfathered detection (level == null)
 *
 * Keep this file in lockstep with the backend module — both sides decide
 * based on `profile.board_level`.
 */

export const LEVEL_CAPS = {
  starter6: 6,
  starter12: 12,
  growing18: 18,
  expanded24: 24,
  advanced30: 30,
  // show_all — no cap. Same runtime behavior as a grandfathered profile,
  // but the parent has explicitly opted in so the picker highlights it.
  show_all: null,
};

export const LEVELS = [
  {
    id: "starter6",
    label: "Starter 6",
    tiles: 6,
    blurb: "6 core needs. Best for first-time AAC users and younger kids.",
    sentenceBuilder: false,
  },
  {
    id: "starter12",
    label: "Starter Voice 12",
    tiles: 12,
    blurb: "Basic communication: yes/no/help plus feelings, food, drink, pain.",
    sentenceBuilder: false,
  },
  {
    id: "growing18",
    label: "Growing Voice 18",
    tiles: 18,
    blurb: "Adds people, requests, and combos. Sentence Builder unlocks here.",
    sentenceBuilder: true,
  },
  {
    id: "expanded24",
    label: "Expanded Voice 24",
    tiles: 24,
    blurb: "Routines for home, school, and play.",
    sentenceBuilder: true,
  },
  {
    id: "advanced30",
    label: "Advanced Voice 30",
    tiles: 30,
    blurb: "Broader vocabulary for users ready for more choices.",
    sentenceBuilder: true,
  },
  {
    id: "show_all",
    label: "Show all",
    tiles: null,
    blurb: "No cap — every tile you've built is shown. Best for boards with 30+ words.",
    sentenceBuilder: true,
  },
];

const VALID = new Set(LEVELS.map((l) => l.id));

/** Tile cap to apply, or null when grandfathered / show_all (no cap). */
export function tileCapFor(level) {
  if (!level) return null;
  return LEVEL_CAPS[level] ?? null;
}

/** Sentence Builder gating. Grandfathered profiles keep it ON (we don't
 * remove a feature an existing family already has). starter6 & starter12
 * hide it by design. */
export function sentenceBuilderEnabledFor(level, sentenceBuilderEnabled) {
  // Per founder-spec (2026-05): Sentence Builder is OFF by default and only
  // turns on when the parent flips `sentence_builder_enabled` from Manage
  // Board → Create a Button (or Modes). The level still gates *availability*
  // (starter6/starter12 can't enable it at all), but enabling is a manual
  // parent choice.
  if (!level) return sentenceBuilderEnabled === true; // grandfathered
  if (!LEVELS.find((l) => l.id === level)?.sentenceBuilder) return false; // not available at this level
  return sentenceBuilderEnabled === true;
}

export function isValidLevel(level) {
  return VALID.has(level);
}

export function levelMeta(level) {
  return LEVELS.find((l) => l.id === level) || null;
}

/** True for profiles created before the field existed — show the
 * "choose a level when you're ready" banner in Manage Board. */
export function isGrandfathered(profile) {
  if (!profile) return false;
  return !profile.board_level || !isValidLevel(profile.board_level);
}
